import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import QRCode from 'qrcode.react';
import copy from 'copy-to-clipboard';  
import { Buffer } from 'buffer';
import { useTranslation } from 'react-i18next';
import { notification } from 'src/components/Notification';
import { DatePickerField } from 'src/components/DatePickerField/DatePickerField';
import TextField from 'src/components/TextField';
import moment from 'moment';
import SelectBox from 'src/components/SelectBox';
import Button from 'src/components/Button';
import { useSetSurveyData } from 'src/hooks/job';
import { parseFormErrors } from 'src/utils/api/error';
import s from './JobFormSurveyTab.Styles';

const JobFormSurveyTab = props => {
  const {
    surveyDatasheets,
  } = props;

  const { t } = useTranslation();

  const setSurveyData = useSetSurveyData();

  const surveyOptions = surveyDatasheets.filter(v => v.currentVersion.survey.surveyData !== null)
    .map(v => ({
      value: v.currentVersion.id,
      label: v.currentVersion.name,
      surveyData: v.currentVersion.survey.surveyData,
      jobId: v.currentVersion.survey.jobId,
      datasheetVersionId: v.currentVersion.survey.datasheetVersionId,
    }))
    .sort((a, b) => a.label > b.label ? 1 : -1);
  const [selectedSurvey, setSelectedSurvey] = useState(surveyOptions[0]);
  
  const [endDate, setEndDate] = useState(selectedSurvey?.surveyData?.endDate);
  const [prizeDrawUrl, setPrizeDrawUrl] = useState(selectedSurvey?.surveyData?.prizeDrawUrl);

  useEffect(() => {
    setEndDate(selectedSurvey?.surveyData?.endDate);
    setPrizeDrawUrl(selectedSurvey?.surveyData?.prizeDrawUrl);
  }, [selectedSurvey])

  const updateSurveyData = async () => {
    const surveyEndDate = endDate ? moment(endDate).format('YYYY-MM-DD') : null;
    try {
      const { data: { setSurveyData: { success } } } = await setSurveyData(selectedSurvey.jobId, selectedSurvey.datasheetVersionId, {
        endDate: surveyEndDate,
        prizeDrawUrl: prizeDrawUrl || null,
      });

      if (success) {
        selectedSurvey.surveyData.endDate = surveyEndDate;
        selectedSurvey.surveyData.prizeDrawUrl = prizeDrawUrl;
        notification.success(t('SURVEY_DATA_UPDATE_SUCCESS'));
      } else {
        notification.error(t('SURVEY_DATA_UPDATE_ERROR'));
      }
    } catch (e) {
      const errors = parseFormErrors(e);
      if (errors.general) {
        notification.error(errors.general);
      }
    }
  };

  const downloadQRCodePdf = () => {
    const svg = document.getElementById('QRCode');
    svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
    const base64doc =  Buffer.from(svg.outerHTML).toString('base64');
    const a = document.createElement('a');
    const e = new MouseEvent('click'); 
    a.download = `${selectedSurvey.label}_QR_Code.svg`;
    a.href = `data:image/svg+xml;base64, ${base64doc}`;
    a.dispatchEvent(e);
  };

  return (
    <s.Block>
      <s.Row>
        <s.Cell label>{t('SURVEY_LABEL')}</s.Cell>
        <s.Cell>
          <SelectBox
            onChange={(selectedOption) => {
              setSelectedSurvey(selectedOption);
            }}
            options={surveyOptions}
            value={surveyOptions.filter(survey => survey.value === selectedSurvey?.value)}
          />
        </s.Cell>
      </s.Row>
      <s.Row>
        <s.Cell label>{t('SURVEY_END_DATE')}</s.Cell>
        <s.Cell>
          <DatePickerField
            dateOnly={true}
            id="endDate"
            onChange={e => setEndDate(e)}
            value={endDate}
          />
        </s.Cell>
      </s.Row>
      <s.Row>
        <s.Cell>{t('SURVEY_LINK_INFO')}</s.Cell>
      </s.Row>
      <s.Row>
        <s.Cell label>{t('SURVEY_EXECUTION_LINK')}</s.Cell>
        <s.Cell>
          <a href={selectedSurvey?.surveyData?.publicLink} target="_blank" rel="noopener noreferrer">{selectedSurvey?.surveyData?.publicLink}</a>
        </s.Cell>
        <s.Cell>
          {selectedSurvey?.surveyData?.publicLink && (
            <s.SmallButton
              onClick={() => copy(selectedSurvey?.surveyData?.publicLink)}
              orange
              rounded
              solid
              type="button"
            >
              {t('COPY')}
            </s.SmallButton>
          )}
        </s.Cell>
      </s.Row>
      <s.Row>
        <s.Cell label>{t('SURVEY_QR_CODE')}</s.Cell>
        <s.Cell>
          {selectedSurvey?.surveyData?.publicLink && (
            <QRCode 
              id="QRCode" 
              width="200px" 
              height="200px" 
              value={selectedSurvey?.surveyData?.publicLink} 
              renderAs="svg"
            />
          )}
        </s.Cell>
        <s.Cell>
          {selectedSurvey?.surveyData?.publicLink && (
            <s.SmallButton
              onClick={() =>downloadQRCodePdf()}
              orange
              rounded
              solid
              type="button"
            >
              {t('DOWNLOAD')}
            </s.SmallButton>
          )}
        </s.Cell>
      </s.Row>
      <s.Row>
        <s.Cell label>{t('SURVEY_PRIZE_DRAW_URL')}</s.Cell>
        <s.Cell>
          <TextField
            id="prizeDrawUrl"
            onChange={e => setPrizeDrawUrl(e.target.value)}
            value={prizeDrawUrl || ''}
          />
        </s.Cell>
      </s.Row>
      <s.Row>
        <s.Cell></s.Cell>
        <s.Cell>
          <Button
            onClick={updateSurveyData}
            orange
            rounded
            solid
            type="button"
            disabled={!selectedSurvey}
            gray={!selectedSurvey}
          >
            {t('SAVE')}
          </Button>
        </s.Cell>
      </s.Row>
    </s.Block>
  );
};

JobFormSurveyTab.propTypes = {
  surveyDatasheets: PropTypes.object,
};

export default React.memo(JobFormSurveyTab);
